import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import DropdownInput from "./DropdownInput";
import { getStudentsForDropdown } from "services/studentServices";
import type { DropdownInputOption } from "types/DropdownInputOption";
import { PictureStudentOption } from "config/constants";
import { Student } from "types/Student";

interface StudentDropdownProps {
  currentClassId: number;
  currentStudentId: string | PictureStudentOption | null;
  onChange: (value: number | PictureStudentOption | null) => void;
}

const StudentDropdown: React.FC<StudentDropdownProps> = ({
  currentClassId,
  currentStudentId,
  onChange,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);

  const fetchStudents = useCallback(async () => {
    if (!currentClassId) {
      setStudents([]);
      return;
    }

    setIsLoading(true);
    try {
      const studentsData = await getStudentsForDropdown(currentClassId);
      setStudents(studentsData.map((student) => student.student_info));
    } catch (e) {
      console.error("Error fetching students:", e);
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentClassId]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const studentOptions: DropdownInputOption[] = [
    {
      label: t("_published_picture._all_students"),
      value: PictureStudentOption.all,
    },
    ...students.map((student) => ({
      label: `${student.last_name} ${student.first_name}`,
      value: student.id.toString(),
    })),
    {
      label: t("_published_picture._class_pictures"),
      value: PictureStudentOption.class,
    },
  ];

  const handleChange = (value: string) => {
    console.log("Selected student value:", value);
    if (
      value === PictureStudentOption.class ||
      value === PictureStudentOption.all
    ) {
      onChange(value);
      return;
    }

    const numericValue = parseInt(value);
    if (!isNaN(numericValue)) {
      onChange(numericValue);
    } else {
      onChange(null);
    }
  };

  return (
    <DropdownInput
      placeholder={t("_published_picture._please_select_student")}
      options={studentOptions}
      value={currentStudentId ?? ""}
      onChange={handleChange}
      disable={!currentClassId || isLoading}
      className="flex-1"
      isFullHeight
    />
  );
};

export default StudentDropdown;
